<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import { useConfigStore, type ServerConfig } from '@designer/stores/configStore';
import { useDesignStore, type ViewName } from '@designer/stores/designStore';
import { useProductStore } from '@designer/stores/productStore';
import { usePrintStore } from '@designer/stores/printStore';
import { useCanvasRenderer } from '@designer/composables/useCanvasRenderer';
import { useDesignApi } from '@designer/composables/useDesignApi';
import { useDesignerPricing } from '@designer/composables/useDesignerPricing';
import ProductAttributes from '@shared/components/ProductAttributes.vue';
import { formatMoney } from '@shared/money';
import { applyPlaceholderAutoFit, readSmartLayout } from '@core/smartLayout';
import { multiHexGradient } from '@core/garmentTint';
import type { DesignItem } from '@core/types';
import type { PublicDesignTemplate } from './types';
import {
  initProductFromConfig,
  scaleTemplateStateToConfig,
  WOO_HOST_DOM,
  type QuickConfig,
  type QuickHostDom,
} from './productMockup';
import { veyopAjaxUrl } from '@core/veyopRuntime';
import { quickPriceAmounts } from './quickPricing';

const props = defineProps<{ configId?: string }>();
const config = useConfigStore();
const design = useDesignStore();
const product = useProductStore();
const print = usePrintStore();
const renderer = useCanvasRenderer();
const designApi = useDesignApi();

type Slot = {
  key: string;
  view: string;
  item: DesignItem;
  label: string;
  role: 'text' | 'image' | 'logo';
  required: boolean;
};
/**
 * Extension contract for addons that add controls to the quick personalizer
 * (e.g. Smart Configurator). Addons push an extension onto
 * `window.veyopQuickExtensions` and dispatch `veyop:quick-extension`.
 */
export type QuickExtensionApi = {
  /** Re-apply extensions, re-render the preview and re-price. */
  invalidate: () => void;
  setProductColor: (index: number) => void;
  config: QuickConfig;
  formatMoney: (amount: number) => string;
};
export type QuickExtension = {
  id: string;
  mount?: (container: HTMLElement, api: QuickExtensionApi) => (() => void) | void;
  /** Mutate the working copy of the template items before each render. */
  transformItems?: (items: Record<string, DesignItem[]>) => void;
  /** Data sent as pricing_context[id] with quotes and add-to-cart. */
  pricingContext?: () => unknown;
  /** Human-readable errors that block add to cart. */
  validate?: () => string[];
  /** Rows shown in the cart/order under the product. */
  options?: () => Array<{ type: string; name: string; value: string }>;
};
declare global {
  interface Window {
    veyopQuickExtensions?: QuickExtension[];
  }
}

type QuickReturnDraft = {
  expiresAt: number;
  templateId: number;
  selections?: Record<string, string>;
  touched?: Record<string, boolean>;
  selectedAttributes?: Record<string, unknown>;
  colorIndex?: number;
  quantity?: number;
};

const ready = ref(false);
const busy = ref(false);
const error = ref('');
const pricingError = ref('');
const variationPending = ref(false);
const showPriceBreakdown = ref(false);
const preview = ref('');
const baseline = ref<PublicDesignTemplate['designer_state'] | null>(null);
const quickConfig = ref<QuickConfig | null>(null);
/** Store product-page selectors (WooCommerce, or the host's from the server config). */
const dom = computed<QuickHostDom>(() => ({ ...WOO_HOST_DOM, ...(quickConfig.value?.hostDom ?? {}) }));
const values = reactive<Record<string, string>>({});
const touched = reactive<Record<string, boolean>>({});
const uploading = reactive<Record<string, boolean>>({});
let renderSequence = 0;
const extensionHost = ref<HTMLElement | null>(null);
const extensions: QuickExtension[] = [];
const extensionCleanups: Array<() => void> = [];

const extensionApi: QuickExtensionApi = {
  invalidate: () => {
    void applyValuesAndRender().then(() => {
      if (ready.value) void refreshQuickPrice();
    });
  },
  setProductColor: (index: number) => changeColor(index),
  get config() {
    return quickConfig.value as QuickConfig;
  },
  formatMoney: (amount: number) => formatMoney(amount, config.data.currency),
};

function attachExtensions(): void {
  if (!quickConfig.value || !extensionHost.value) return;
  for (const extension of window.veyopQuickExtensions ?? []) {
    if (!extension?.id || extensions.some((known) => known.id === extension.id)) continue;
    extensions.push(extension);
    const container = document.createElement('div');
    container.dataset.veyopQuickExtension = extension.id;
    extensionHost.value.appendChild(container);
    const cleanup = extension.mount?.(container, extensionApi);
    if (typeof cleanup === 'function') extensionCleanups.push(cleanup);
  }
  if (ready.value) extensionApi.invalidate();
}

function syncExtensionContext(): void {
  const context: Record<string, unknown> = {};
  for (const extension of extensions) {
    const value = extension.pricingContext?.();
    if (value !== undefined && value !== null) context[extension.id] = value;
  }
  if (JSON.stringify(context) !== JSON.stringify(config.data.pricingContext ?? {})) {
    config.data.pricingContext = context;
  }
}

const slots = computed<Slot[]>(() => {
  const state = baseline.value;
  if (!state) return [];
  const result: Slot[] = [];
  for (const view of state.views) {
    for (const raw of state.items[view] ?? []) {
      const item = raw as DesignItem;
      const layout = readSmartLayout(item);
      if (!layout.placeholder || !['text', 'image', 'svg'].includes(item.type)) continue;
      const role = item.type === 'text'
        ? 'text'
        : layout.placeholderRole === 'logo' ? 'logo' : 'image';
      const customName = String((item as Record<string, unknown>).name ?? '').trim();
      result.push({
        key: `${view}:${item.id}`,
        view,
        item,
        label: customName || (item.type === 'text' ? String(item.text || 'Text') : role === 'logo' ? 'Logo' : 'Image'),
        role,
        required: layout.required === true,
      });
    }
  }
  return result;
});

const colors = computed(() => product.product?.colors ?? []);
const attributes = computed(() => product.product?.attributes ?? []);
const replaceProductImage = computed(() => quickConfig.value?.replaceProductImage === true);
const priceAmounts = computed(() => quickPriceAmounts(product.price, product.oldPrice, product.quantity));
const formattedLineTotal = computed(() => formatMoney(priceAmounts.value.lineTotal, config.data.currency));
const formattedOldLineTotal = computed(() => formatMoney(priceAmounts.value.oldLineTotal, config.data.currency));
const priceComponents = computed(() => product.breakdown?.components ?? []);
const pricing = useDesignerPricing({
  forceEnabled: true,
  beforeRefresh: syncWooForm,
  canSchedule: () => ready.value,
});

type WooJQueryHandle = {
  on: (events: string, handler: (...args: unknown[]) => void) => WooJQueryHandle;
  off: (events: string, handler: (...args: unknown[]) => void) => WooJQueryHandle;
  trigger: (event: string) => WooJQueryHandle;
  removeData: (name: string) => WooJQueryHandle;
};
type WooJQuery = (target: Element) => WooJQueryHandle;

let wooForm: HTMLFormElement | null = null;
let wooJQueryForm: WooJQueryHandle | null = null;
let wooSyncTimer: number | null = null;
let wooQuantityFrame: number | null = null;

function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function quickReturnStorageKey(c: QuickConfig): string {
  return `veyop_quick_return_${c.productId}_${c.assignedTemplate.id}`;
}

function consumeQuickReturnDraft(c: QuickConfig): QuickReturnDraft | null {
  const key = quickReturnStorageKey(c);
  const raw = sessionStorage.getItem(key);
  if (!raw) return null;
  sessionStorage.removeItem(key);
  try {
    const draft = JSON.parse(raw) as QuickReturnDraft;
    if (
      draft.templateId !== c.assignedTemplate.id
      || !Number.isFinite(draft.expiresAt)
      || draft.expiresAt < Date.now()
    ) {
      return null;
    }
    return draft;
  } catch {
    return null;
  }
}

function initProduct(c: QuickConfig): void {
  initProductFromConfig(c, product);
  product.setQuantity(1);
  print.setPrintType(c.printType);
}

async function applyValuesAndRender(colorIndex = product.activeColorIndex): Promise<void> {
  const state = baseline.value;
  if (!state) return;
  const sequence = ++renderSequence;
  const items = clone(state.items) as Record<string, DesignItem[]>;
  for (const slot of slots.value) {
    const item = items[slot.view]?.find((candidate) => candidate.id === slot.item.id);
    if (!item) continue;
    const value = values[slot.key];
    if (slot.role === 'text') {
      const patch = applyPlaceholderAutoFit(item, { text: value }) ?? { text: value };
      Object.assign(item, patch);
    } else if (value) {
      const dimensions = await imageDimensions(value);
      if (sequence !== renderSequence) return;
      const patch = applyPlaceholderAutoFit(item, {
        src: value,
        sourcePixelWidth: dimensions.width,
        sourcePixelHeight: dimensions.height,
      }) ?? { src: value };
      Object.assign(item, patch);
      if (touched[slot.key]) {
        Object.assign(item, {
          upload: 1,
          file_name: value.split('/').pop()?.split('?')[0] || 'upload',
          file: value,
          thumb: value,
          large: value,
        });
      }
    }
  }
  for (const extension of extensions) {
    extension.transformItems?.(items);
  }
  design.setAvailableViews(state.views as ViewName[]);
  design.loadDesignState({
    items,
    activeView: state.activeView,
    designId: null,
    designKey: '',
    designerId: '',
    designFile: '',
    origin: 'template',
  });
  await nextTick();
  const canvas = await renderer.renderProductMockup(design.activeView, colorIndex);
  if (!canvas || sequence !== renderSequence) return;
  preview.value = productMockupDataUrl(canvas);
  if (replaceProductImage.value) {
    syncWooGallery(preview.value, canvas.width, canvas.height);
  }
}

function productMockupDataUrl(source: HTMLCanvasElement): string {
  const output = document.createElement('canvas');
  output.width = source.width;
  output.height = source.height;
  const context = output.getContext('2d');
  if (!context) return source.toDataURL('image/jpeg', 0.9);
  context.fillStyle = '#fff';
  context.fillRect(0, 0, output.width, output.height);
  context.drawImage(source, 0, 0);
  return output.toDataURL('image/jpeg', 0.9);
}

function imageDimensions(src: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve) => {
    const image = new Image();
    image.onload = () => resolve({ width: image.naturalWidth || 1, height: image.naturalHeight || 1 });
    image.onerror = () => resolve({ width: 1, height: 1 });
    image.src = src;
  });
}

function syncWooGallery(src: string, width: number, height: number): void {
  const image = document.querySelector<HTMLImageElement>(dom.value.galleryImage);
  if (!image) return;

  const fullWidth = Math.max(1, Math.round(width));
  const fullHeight = Math.max(1, Math.round(height));
  const slide = image.closest<HTMLElement>('.woocommerce-product-gallery__image');
  const gallery = image.closest<HTMLElement>('.woocommerce-product-gallery');
  const link = image.closest<HTMLAnchorElement>('a');
  const jq = (window as unknown as { jQuery?: WooJQuery }).jQuery;

  if (jq && slide) {
    jq(slide).trigger('zoom.destroy');
  }
  slide?.querySelector<HTMLImageElement>('.zoomImg')?.remove();

  image.setAttribute('data-src', src);
  image.setAttribute('data-large_image', src);
  image.setAttribute('data-large_image_width', String(fullWidth));
  image.setAttribute('data-large_image_height', String(fullHeight));
  image.setAttribute('width', String(fullWidth));
  image.setAttribute('height', String(fullHeight));
  image.srcset = '';
  image.removeAttribute('sizes');
  link?.setAttribute('href', src);
  slide?.setAttribute('data-thumb', src);

  const galleryThumbnail = gallery?.querySelector<HTMLImageElement>('.flex-control-nav li:first-child img');
  if (galleryThumbnail) {
    galleryThumbnail.src = src;
    galleryThumbnail.srcset = '';
  }

  if (jq) {
    jq(image)
      .removeData('src')
      .removeData('large_image')
      .removeData('large_image_width')
      .removeData('large_image_height');
  }

  let initialized = false;
  const initializeZoom = (): void => {
    if (initialized) return;
    initialized = true;
    image.removeEventListener('load', initializeZoom);
    if (!jq || !gallery) return;
    jq(gallery)
      .trigger('woocommerce_gallery_reset_slide_position')
      .trigger('woocommerce_gallery_init_zoom');
  };
  image.addEventListener('load', initializeZoom);
  image.src = src;
  if (image.complete) {
    window.setTimeout(initializeZoom, 0);
  }
}

function wooPriceTargets(): HTMLElement[] {
  const candidates = Array.from(document.querySelectorAll<HTMLElement>(dom.value.priceTargets.join(', ')));
  return candidates.filter((candidate) =>
    !candidates.some((other) => other !== candidate && other.contains(candidate))
    && !candidate.closest('.related, .upsells, .cross-sells, [data-is-descendent-of-query-loop="true"]'),
  );
}

function appendFormattedPrice(target: HTMLElement, oldPrice: number, price: number, onSale: boolean): void {
  target.querySelector(':scope > [data-veyop-live-price]')?.remove();
  let original = target.querySelector<HTMLElement>(':scope > [data-veyop-original-price]');
  if (!original) {
    original = document.createElement('span');
    original.className = 'veyop-original-unit-price';
    original.dataset.veyopOriginalPrice = '1';
    original.append(...Array.from(target.childNodes));
    target.append(original);
  }
  const live = document.createElement('span');
  live.className = 'veyop-live-unit-price';
  live.dataset.veyopLivePrice = '1';
  live.setAttribute('aria-live', 'polite');
  if (onSale) {
    const old = document.createElement('del');
    old.textContent = formatMoney(oldPrice, config.data.currency);
    const current = document.createElement('ins');
    current.textContent = formatMoney(price, config.data.currency);
    live.append(old, document.createTextNode(' '), current);
  } else {
    live.textContent = formatMoney(price, config.data.currency);
  }
  if (product.priceSuffix) {
    const suffix = document.createElement('small');
    suffix.className = 'veyop-live-price-suffix';
    suffix.textContent = ` ${product.priceSuffix}`;
    live.append(suffix);
  }
  target.classList.add('veyop-live-price-target');
  target.append(live);
}

function syncWooPriceDisplay(): void {
  if (product.priceStatus !== 'ready') return;
  const amounts = priceAmounts.value;
  for (const target of wooPriceTargets()) {
    appendFormattedPrice(target, amounts.oldUnitPrice, amounts.unitPrice, amounts.onSale);
  }
}

function restoreWooPriceDisplay(): void {
  for (const target of document.querySelectorAll<HTMLElement>('.veyop-live-price-target')) {
    target.querySelector(':scope > [data-veyop-live-price]')?.remove();
    const original = target.querySelector<HTMLElement>(':scope > [data-veyop-original-price]');
    if (original) {
      while (original.firstChild) target.insertBefore(original.firstChild, original);
      original.remove();
    }
    target.classList.remove('veyop-live-price-target');
  }
}

function setWooCartQuoteState(state: 'idle' | 'loading' | 'ready' | 'unavailable'): void {
  const button = document.querySelector<HTMLElement>(dom.value.cartButton);
  if (!button) return;
  button.dataset.veyopQuoteState = state;
}

function clearWooCartQuoteState(): void {
  document.querySelector<HTMLElement>(dom.value.cartButton)
    ?.removeAttribute('data-veyop-quote-state');
}

function changeColor(index: number): void {
  product.changeColor(index);
  void applyValuesAndRender(index);
}

function onText(slot: Slot, event: Event): void {
  values[slot.key] = (event.target as HTMLInputElement).value;
  touched[slot.key] = true;
  void applyValuesAndRender();
}

async function uploadFile(file: File): Promise<string> {
  const maxSizeMb = Math.max(1, Number(config.data.uploadMax || 10));
  if (file.size > maxSizeMb * 1024 * 1024) throw new Error(`File is larger than ${maxSizeMb} MB.`);
  const form = new FormData();
  form.append('myfile', file);
  if (config.data.csrfToken) form.append('_token', config.data.csrfToken);
  // ajax.php?type=upload on WordPress, the host API's ajax/upload route on OpenCart / PrestaShop.
  const response = await fetch(veyopAjaxUrl('upload', {}, String(config.data.veyopUrl || '')), {
    method: 'POST',
    body: form,
    credentials: 'same-origin',
    headers: config.data.csrfToken ? { 'X-CSRF-Token': config.data.csrfToken } : {},
  });
  const data = await response.json().catch(() => ({})) as {
    status?: number;
    src?: string;
    msg?: string;
    error?: string;
    item?: { original?: string; medium?: string };
  };
  const url = String(data.item?.medium || data.item?.original || data.src || '');
  if (!response.ok || Number(data.status || 0) !== 1 || !url) {
    throw new Error(String(data.error || data.msg || 'Upload failed.'));
  }
  return url;
}

async function onFile(slot: Slot, event: Event): Promise<void> {
  const file = (event.target as HTMLInputElement).files?.[0];
  if (!file) return;
  if (!file.type.startsWith('image/')) {
    error.value = 'Choose an image file.';
    return;
  }
  error.value = '';
  uploading[slot.key] = true;
  try {
    values[slot.key] = await uploadFile(file);
    touched[slot.key] = true;
    await applyValuesAndRender();
  } catch (cause) {
    touched[slot.key] = false;
    error.value = cause instanceof Error ? cause.message : 'Could not upload image.';
  } finally {
    uploading[slot.key] = false;
  }
}

function syncWooForm(): boolean {
  // Extension selections (e.g. Smart Configurator) ride along with every quote.
  syncExtensionContext();
  const form = wooForm ?? document.querySelector<HTMLFormElement>(dom.value.cartForm);
  const qty = Number(form?.querySelector<HTMLInputElement>(dom.value.quantityInput)?.value || 1);
  product.setQuantity(qty);
  const variationId = form?.querySelector<HTMLInputElement>('input.variation_id')?.value || '';
  const variation: string[] = [];
  form?.querySelectorAll<HTMLSelectElement>('select[name^="attribute_"]').forEach((select) => {
    if (select.value) variation.push(`${select.name}|${select.value}`);
  });
  config.data.variationId = variationId;
  config.data.variationAttributes = variation.join(';');
  const valid = !form?.classList.contains('variations_form') || Number(variationId) > 0;
  variationPending.value = !valid;
  if (!valid) {
    restoreWooPriceDisplay();
  }
  return valid;
}

function scheduleWooPriceRefresh(): void {
  if (wooSyncTimer !== null) window.clearTimeout(wooSyncTimer);
  wooSyncTimer = window.setTimeout(() => {
    wooSyncTimer = null;
    pricing.scheduleRefresh();
  }, 0);
}

function onWooFormInput(event: Event): void {
  const target = event.target as Element | null;
  if (!target?.matches(`${dom.value.quantityInput}, select[name^="attribute_"]`)) return;
  scheduleWooPriceRefresh();
}

function onWooQuantityInteraction(event: Event): void {
  const target = event.target as Element | null;
  if (!target?.closest('.quantity')) return;
  if (wooQuantityFrame !== null) window.cancelAnimationFrame(wooQuantityFrame);
  wooQuantityFrame = window.requestAnimationFrame(() => {
    wooQuantityFrame = null;
    scheduleWooPriceRefresh();
  });
}

function onWooVariationEvent(): void {
  scheduleWooPriceRefresh();
}

function bindWooPricingEvents(): void {
  wooForm = document.querySelector<HTMLFormElement>(dom.value.cartForm);
  wooForm?.addEventListener('input', onWooFormInput);
  wooForm?.addEventListener('change', onWooFormInput);
  wooForm?.addEventListener('click', onWooQuantityInteraction);
  wooForm?.addEventListener('keyup', onWooQuantityInteraction);
  const jq = (window as unknown as { jQuery?: WooJQuery }).jQuery;
  if (jq && wooForm) {
    wooJQueryForm = jq(wooForm);
    wooJQueryForm.on(
      'found_variation.veyopQuick reset_data.veyopQuick woocommerce_variation_has_changed.veyopQuick',
      onWooVariationEvent,
    );
  }
}

function unbindWooPricingEvents(): void {
  wooForm?.removeEventListener('input', onWooFormInput);
  wooForm?.removeEventListener('change', onWooFormInput);
  wooForm?.removeEventListener('click', onWooQuantityInteraction);
  wooForm?.removeEventListener('keyup', onWooQuantityInteraction);
  wooJQueryForm?.off(
    'found_variation.veyopQuick reset_data.veyopQuick woocommerce_variation_has_changed.veyopQuick',
    onWooVariationEvent,
  );
  wooJQueryForm = null;
  wooForm = null;
  if (wooSyncTimer !== null) {
    window.clearTimeout(wooSyncTimer);
    wooSyncTimer = null;
  }
  if (wooQuantityFrame !== null) {
    window.cancelAnimationFrame(wooQuantityFrame);
    wooQuantityFrame = null;
  }
}

async function refreshQuickPrice(): Promise<boolean> {
  pricingError.value = '';
  const quoted = await pricing.refreshPriceNow();
  if (!quoted) {
    if (!variationPending.value) {
      pricingError.value = 'Price unavailable. Please try again.';
    }
    restoreWooPriceDisplay();
    return false;
  }
  pricingError.value = '';
  syncWooPriceDisplay();
  return true;
}

function retryPricing(): void {
  void refreshQuickPrice();
}

watch(
  () => [product.priceStatus, product.price, product.oldPrice, product.quantity, product.priceSuffix] as const,
  ([status]) => {
    setWooCartQuoteState(status);
    if (status === 'ready') {
      pricingError.value = '';
      syncWooPriceDisplay();
      return;
    }
    restoreWooPriceDisplay();
    if (status === 'unavailable' && !variationPending.value) {
      pricingError.value = 'Price unavailable. Please try again.';
    }
  },
  { flush: 'post' },
);

function quickOptions(): Array<{ type: string; name: string; value: string }> {
  return [
    ...slots.value.map((slot) => ({
      type: slot.role === 'text' ? 'text' : 'image',
      name: slot.label,
      value: slot.role === 'text' ? values[slot.key] : (touched[slot.key] ? 'Uploaded image' : 'Template image'),
    })),
    ...extensions.flatMap((extension) => extension.options?.() ?? []),
  ];
}

async function addToCart(event: Event): Promise<void> {
  event.preventDefault();
  event.stopImmediatePropagation();
  if (!ready.value || busy.value) return;
  error.value = '';
  if (Object.values(uploading).some(Boolean)) {
    error.value = 'Wait for image uploads to finish.';
    return;
  }
  const missing = slots.value.filter((slot) => slot.required && !touched[slot.key]);
  if (missing.length) {
    error.value = `Complete: ${missing.map((slot) => slot.label).join(', ')}`;
    return;
  }
  const extensionErrors = extensions.flatMap((extension) => extension.validate?.() ?? []);
  if (extensionErrors.length) {
    error.value = extensionErrors.join(' ');
    return;
  }
  busy.value = true;
  try {
    if (!(await refreshQuickPrice())) {
      error.value = variationPending.value
        ? 'Choose a product variation before adding to cart.'
        : 'Could not confirm the personalized price. Retry pricing before adding to cart.';
      return;
    }
    const extension = config.data as ServerConfig & { quickPersonalization?: Record<string, unknown> };
    extension.quickPersonalization = {
      template_id: Number(quickConfig.value?.assignedTemplate.id || 0),
      template_updated_at: String(quickConfig.value?.assignedTemplate.updated_at || ''),
      selections: clone(values),
      options: quickOptions(),
    };
    const result = await designApi.addToCart();
    if (!result.success) {
      error.value = 'Could not add personalized product to cart.';
      return;
    }
    window.location.href = result.redirect || '/cart/';
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : 'Could not add personalized product to cart.';
  } finally {
    busy.value = false;
  }
}

function openAdvanced(): void {
  if (!quickConfig.value?.advancedUrl) return;
  syncWooForm();
  const token = crypto.randomUUID();
  const draft = {
    expiresAt: Date.now() + 60 * 60 * 1000,
    templateId: quickConfig.value.assignedTemplate.id,
    items: clone(design.items),
    views: [...design.views],
    activeView: design.activeView,
    selectedAttributes: clone(product.selectedAttributes),
    colorIndex: product.activeColorIndex,
    quantity: product.quantity,
    variationId: config.data.variationId,
    variationAttributes: config.data.variationAttributes,
  };
  sessionStorage.setItem(`veyop_quick_draft_${token}`, JSON.stringify(draft));
  sessionStorage.setItem(quickReturnStorageKey(quickConfig.value), JSON.stringify({
    expiresAt: draft.expiresAt,
    templateId: draft.templateId,
    selections: clone(values),
    touched: clone(touched),
    selectedAttributes: draft.selectedAttributes,
    colorIndex: draft.colorIndex,
    quantity: draft.quantity,
  }));
  const url = new URL(quickConfig.value.advancedUrl, window.location.origin);
  url.searchParams.set('quick_draft', token);
  window.location.href = url.toString();
}

function captureCartClick(event: Event): void {
  const target = event.target as Element | null;
  if (target?.closest(dom.value.cartButton)) {
    void addToCart(event);
  }
}

function restoreQuickPreview(event: PageTransitionEvent): void {
  if (!event.persisted || !ready.value) return;
  if (quickConfig.value) {
    sessionStorage.removeItem(quickReturnStorageKey(quickConfig.value));
  }
  void applyValuesAndRender();
}

onMounted(async () => {
  document.addEventListener('click', captureCartClick, true);
  window.addEventListener('veyop:quick-extension', attachExtensions);
  window.addEventListener('pageshow', restoreQuickPreview);
  const element = props.configId ? document.getElementById(props.configId) : null;
  if (!element) {
    error.value = 'Quick personalization configuration is missing.';
    return;
  }
  try {
    const parsed = JSON.parse(element.textContent || '{}') as QuickConfig;
    quickConfig.value = parsed;
    config.init(parsed as unknown as Record<string, unknown>);
    bindWooPricingEvents();
    initProduct(parsed);
    baseline.value = scaleTemplateStateToConfig(clone(parsed.assignedTemplate.designer_state), parsed);
    for (const slot of slots.value) {
      values[slot.key] = slot.role === 'text' ? String(slot.item.text || '') : String(slot.item.src || '');
      touched[slot.key] = false;
    }
    const returnDraft = consumeQuickReturnDraft(parsed);
    if (returnDraft) {
      Object.assign(values, returnDraft.selections ?? {});
      Object.assign(touched, returnDraft.touched ?? {});
      if (
        Number.isInteger(returnDraft.colorIndex)
        && Number(returnDraft.colorIndex) >= 0
        && Number(returnDraft.colorIndex) < colors.value.length
      ) {
        product.changeColor(Number(returnDraft.colorIndex));
      }
      if (returnDraft.selectedAttributes) {
        product.setAttributes(returnDraft.selectedAttributes as Parameters<typeof product.setAttributes>[0]);
      }
      if (Number(returnDraft.quantity) > 0) {
        product.setQuantity(Number(returnDraft.quantity));
      }
    }
    attachExtensions();
    await applyValuesAndRender();
    ready.value = true;
    await refreshQuickPrice();
  } catch (cause) {
    error.value = cause instanceof Error ? cause.message : 'Could not start quick personalization.';
  }
});

onBeforeUnmount(() => {
  document.removeEventListener('click', captureCartClick, true);
  window.removeEventListener('veyop:quick-extension', attachExtensions);
  extensionCleanups.splice(0).forEach((cleanup) => cleanup());
  window.removeEventListener('pageshow', restoreQuickPreview);
  unbindWooPricingEvents();
  restoreWooPriceDisplay();
  clearWooCartQuoteState();
});
</script>

<template>
  <section class="vptw:rounded-2xl vptw:border vptw:border-(--vs-line) vptw:bg-white vptw:p-4 vptw:shadow-sm">
    <header class="vptw:mb-4">
      <h3 class="vptw:m-0! vptw:text-base vptw:font-semibold vptw:text-(--vs-ink)">Personalize this design</h3>
      <p class="vptw:m-0! vptw:mt-1! vptw:text-xs vptw:text-(--vs-ink-2)">{{ quickConfig?.assignedTemplate.title }}</p>
    </header>
    <div v-if="preview && !replaceProductImage" class="vptw:mb-4 vptw:flex vptw:justify-center vptw:rounded-xl vptw:bg-gray-50 vptw:p-2">
      <img :src="preview" alt="Personalized product preview" class="vptw:max-h-80 vptw:w-auto vptw:max-w-full vptw:object-contain" />
    </div>
    <div v-if="colors.length > 1" class="vptw:mb-4">
      <p class="vptw:mb-2! vptw:text-sm vptw:font-medium">Product color</p>
      <div class="vptw:flex vptw:flex-wrap vptw:gap-2">
        <button v-for="(color, index) in colors" :key="color.hex" type="button" :title="color.title" class="vptw:size-9 vptw:rounded-full vptw:border-2 vptw:cursor-pointer" :class="product.activeColorIndex === index ? 'vptw:border-(--vs-accent)' : 'vptw:border-(--vs-line)'" :style="{ background: multiHexGradient(color.hex) }" @click="changeColor(index)" />
      </div>
    </div>
    <ProductAttributes v-if="attributes.length" :attributes="attributes" :model-value="product.selectedAttributes" :currency="config.data.currency" :currency-symbol="config.data.currency.symbol" @update:model-value="product.setAttributes($event)" />
    <div class="vptw:mt-4 vptw:rounded-xl vptw:border vptw:border-(--vs-line) vptw:bg-gray-50 vptw:p-3" aria-live="polite">
      <div v-if="product.priceStatus === 'ready'" class="vptw:flex vptw:items-start vptw:justify-between vptw:gap-3">
        <div>
          <p class="vptw:m-0! vptw:text-sm vptw:font-semibold vptw:text-(--vs-ink)">Personalized total</p>
          <p class="vptw:m-0! vptw:mt-0.5! vptw:text-xs vptw:text-(--vs-ink-2)">Quantity {{ priceAmounts.quantity }}</p>
        </div>
        <div class="vptw:text-right">
          <p class="vptw:m-0! vptw:text-base vptw:font-semibold vptw:text-(--vs-ink)">
            <del v-if="priceAmounts.onSale" class="vptw:me-1 vptw:text-xs vptw:font-normal vptw:text-(--vs-ink-3)">{{ formattedOldLineTotal }}</del>
            <span>{{ formattedLineTotal }}</span>
          </p>
          <p v-if="product.priceSuffix" class="vptw:m-0! vptw:text-[11px] vptw:text-(--vs-ink-2)">{{ product.priceSuffix }}</p>
        </div>
      </div>
      <p v-else-if="variationPending" class="vptw:m-0! vptw:text-sm vptw:text-(--vs-ink-2)">Choose product options to calculate price.</p>
      <p v-else-if="product.priceStatus === 'loading'" class="vptw:m-0! vptw:text-sm vptw:text-(--vs-ink-2)">Calculating price…</p>
      <div v-else-if="product.priceStatus === 'unavailable'" role="alert" class="vptw:flex vptw:items-center vptw:justify-between vptw:gap-3">
        <p class="vptw:m-0! vptw:text-sm vptw:text-red-600">{{ pricingError || 'Price unavailable. Please try again.' }}</p>
        <button type="button" class="vptw:shrink-0 vptw:rounded-lg vptw:border vptw:border-red-200 vptw:bg-white vptw:px-3 vptw:py-1.5 vptw:text-xs vptw:font-medium vptw:text-red-700 vptw:cursor-pointer" @click="retryPricing">Retry</button>
      </div>
      <p v-else class="vptw:m-0! vptw:text-sm vptw:text-(--vs-ink-2)">Calculating price…</p>

      <template v-if="product.priceStatus === 'ready' && priceComponents.length">
        <button type="button" class="vptw:mt-2 vptw:border-0 vptw:bg-transparent vptw:p-0 vptw:text-xs vptw:font-medium vptw:text-(--vs-accent) vptw:cursor-pointer" :aria-expanded="showPriceBreakdown" @click="showPriceBreakdown = !showPriceBreakdown">
          {{ showPriceBreakdown ? 'Hide price details' : 'Show price details' }}
        </button>
        <ul v-if="showPriceBreakdown" class="vptw:m-0! vptw:mt-2! vptw:list-none vptw:space-y-1 vptw:border-t vptw:border-(--vs-line) vptw:p-0! vptw:pt-2!">
          <li v-for="(line, index) in priceComponents" :key="line.key ?? index" class="vptw:flex vptw:justify-between vptw:gap-3 vptw:text-xs vptw:text-(--vs-ink-2)">
            <span>{{ line.label }}<span v-if="(line.qty ?? 1) > 1"> × {{ line.qty }}</span></span>
            <span class="vptw:font-medium vptw:text-(--vs-ink)">{{ formatMoney(line.total ?? 0, config.data.currency) }}</span>
          </li>
        </ul>
      </template>
    </div>
    <div class="vptw:mt-4 vptw:space-y-3">
      <label v-for="slot in slots" :key="slot.key" class="vptw:block">
        <span class="vptw:mb-1 vptw:block vptw:text-sm vptw:font-medium">{{ slot.label }} <span v-if="slot.required" class="vptw:text-red-500">*</span></span>
        <input v-if="slot.role === 'text'" type="text" :value="values[slot.key]" class="vptw:w-full vptw:rounded-xl vptw:border vptw:border-(--vs-line) vptw:px-3 vptw:py-2" @input="onText(slot, $event)" />
        <input v-else type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" class="vptw:block vptw:w-full vptw:text-sm" :disabled="uploading[slot.key]" @change="onFile(slot, $event)" />
        <span v-if="uploading[slot.key]" class="vptw:mt-1 vptw:block vptw:text-xs vptw:text-(--vs-ink-2)">Uploading…</span>
      </label>
    </div>
    <div ref="extensionHost" class="vptw:mt-4 vptw:space-y-4 vptw:empty:hidden" />
    <p v-if="error" role="alert" class="vptw:mt-3! vptw:text-sm vptw:text-red-600">{{ error }}</p>
    <button v-if="quickConfig?.advancedUrl" type="button" class="vptw:mt-4 vptw:rounded-xl vptw:border vptw:border-(--vs-line) vptw:bg-white vptw:px-4 vptw:py-2 vptw:text-sm vptw:font-medium vptw:cursor-pointer" @click="openAdvanced">Edit advanced</button>
    <p v-if="busy" class="vptw:mt-2! vptw:text-xs vptw:text-(--vs-ink-2)">Preparing personalized product…</p>
  </section>
</template>
